/**
 * BiologyUploadScanner — Sprint 6
 *
 * Composant patient pour uploader son bilan pré-chimio.
 * Multi-photos → compression → stitch PDF côté client via pdf-lib →
 * envoi vers POST /api/patient/biology/pre-chemo.
 *
 * Props :
 *   - onClose()       : fermeture modal
 *   - onSuccess(data) : appelé après upload OK
 */
import React, { useRef, useState } from "react";
import { PDFDocument } from "pdf-lib";
import { Camera, Image as ImageIcon, X, Check, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";
import { useLanguage } from "@/i18n/index";

const MAX_PAGES = 10;
const MAX_DIMENSION = 1600; // px (largeur ou hauteur max après compression)
const JPEG_QUALITY = 0.82;

// Compresse une image (File) en JPEG redimensionné via Canvas.
// Retourne { bytes, width, height }.
async function compressImageToJpeg(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new window.Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });

  let { width, height } = img;
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise(resolve =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY)
  );
  const bytes = new Uint8Array(await blob.arrayBuffer());
  return { bytes, width, height };
}

// Assemble les images en 1 PDF (pdf-lib).
async function stitchImagesToPdf(pages) {
  const pdfDoc = await PDFDocument.create();
  for (const { bytes, width, height } of pages) {
    const image = await pdfDoc.embedJpg(bytes);
    const page = pdfDoc.addPage([width, height]);
    page.drawImage(image, { x: 0, y: 0, width, height });
  }
  return await pdfDoc.save();
}

export default function BiologyUploadScanner({ onClose, onSuccess }) {
  const { t } = useLanguage();
  const [pages, setPages] = useState([]); // [{ id, previewUrl, bytes, width, height }]
  const [pdfFile, setPdfFile] = useState(null); // { file, name, sizeKB } — mode PDF direct
  const [submitting, setSubmitting] = useState(false);
  const [sampleDate, setSampleDate] = useState(new Date().toISOString().split("T")[0]);
  const fileInputRef = useRef(null);

  const handleAddFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;

    // Détection PDF direct (1 seul fichier PDF → mode PDF)
    const firstIsPdf = files[0].type === "application/pdf";
    if (firstIsPdf) {
      if (files.length > 1) {
        toast.error(t("biology.onlyOnePdf"));
        return;
      }
      if (pages.length > 0) {
        toast.error(t("biology.removePhotosFirst"));
        return;
      }
      if (files[0].size > 20 * 1024 * 1024) {
        toast.error(t("biology.pdfTooLarge"));
        return;
      }
      setPdfFile({
        file: files[0],
        name: files[0].name || "document.pdf",
        sizeKB: Math.round(files[0].size / 1024),
      });
      return;
    }

    // Mode photos
    if (pdfFile) {
      toast.error(t("biology.removePdfFirst"));
      return;
    }
    if (pages.length + files.length > MAX_PAGES) {
      toast.error(t("biology.maxPagesReached"));
      return;
    }

    const toastId = toast.loading(t("biology.imageProcessing"));
    try {
      const newPages = [];
      for (const f of files) {
        if (!f.type.startsWith("image/")) {
          continue;
        }
        const { bytes, width, height } = await compressImageToJpeg(f);
        newPages.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          previewUrl: URL.createObjectURL(new Blob([bytes], { type: "image/jpeg" })),
          bytes,
          width,
          height,
        });
      }
      setPages(prev => [...prev, ...newPages]);
      toast.dismiss(toastId);
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(t("biology.imageError"));
    }
  };

  const removePage = (id) => {
    setPages(prev => {
      const next = prev.filter(p => p.id !== id);
      const removed = prev.find(p => p.id === id);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  };

  const removePdf = () => setPdfFile(null);

  const handleSubmit = async () => {
    if (!pdfFile && pages.length === 0) {
      toast.error(t("biology.needAtLeastOnePage"));
      return;
    }
    setSubmitting(true);
    const toastId = toast.loading(t("biology.uploading"));
    try {
      let fileToSend;
      let fileName;
      if (pdfFile) {
        // Mode PDF direct : aucun stitch, on envoie tel quel
        fileToSend = pdfFile.file;
        fileName = pdfFile.name;
      } else {
        const pdfBytes = await stitchImagesToPdf(pages);
        fileToSend = new Blob([pdfBytes], { type: "application/pdf" });
        fileName = `bilan_${Date.now()}.pdf`;
      }

      const formData = new FormData();
      formData.append("file", fileToSend, fileName);
      formData.append("sampleDate", sampleDate);

      const { data } = await api.post("/patient/biology/pre-chemo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.dismiss(toastId);
      toast.success(t("biology.uploadSuccess"), {
        description: t("biology.uploadSuccessDesc"),
        duration: 6000,
      });
      pages.forEach(p => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      onSuccess?.(data);
      onClose?.();
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err.response?.data?.detail || t("biology.uploadError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" data-testid="biology-upload-scanner">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              {t("biology.title")}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {t("biology.subtitle").replace("{n}", MAX_PAGES)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded-lg"
            data-testid="scanner-close-btn"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t("biology.sampleDate")}
            </label>
            <input
              type="date"
              value={sampleDate}
              onChange={e => setSampleDate(e.target.value)}
              max={new Date().toISOString().split("T")[0]}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              data-testid="scanner-sample-date"
            />
          </div>

          {/* Pages grid (mode photos) */}
          {pages.length > 0 && (
            <div>
              <p className="text-sm font-medium text-slate-700 mb-2">
                {pages.length} {t("biology.pagesAdded")}
              </p>
              <div className="grid grid-cols-3 gap-3">
                {pages.map((p, idx) => (
                  <div
                    key={p.id}
                    className="relative aspect-[3/4] rounded-xl overflow-hidden border-2 border-slate-200 bg-slate-50"
                    data-testid={`scanner-page-${idx}`}
                  >
                    <img
                      src={p.previewUrl}
                      alt={`Page ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute top-1 left-1 bg-white/90 rounded px-1.5 py-0.5 text-[10px] font-bold text-slate-700">
                      {idx + 1}
                    </span>
                    <button
                      onClick={() => removePage(p.id)}
                      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md"
                      data-testid={`scanner-remove-${idx}`}
                      aria-label={t("biology.removePageAria")}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PDF direct */}
          {pdfFile && (
            <div className="border-2 border-emerald-300 bg-emerald-50 rounded-xl p-4 flex items-center gap-3" data-testid="scanner-pdf-selected">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border border-emerald-200">
                <FileText className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{pdfFile.name}</p>
                <p className="text-xs text-slate-500">{t("biology.pdfBadge")} · {pdfFile.sizeKB} Ko</p>
              </div>
              <button
                onClick={removePdf}
                className="p-1.5 bg-white hover:bg-red-50 border border-slate-200 rounded-lg text-red-500"
                aria-label={t("biology.removePdfAria")}
                data-testid="scanner-remove-pdf"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Add buttons */}
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => {
                fileInputRef.current.setAttribute("accept", "image/*");
                fileInputRef.current.setAttribute("capture", "environment");
                fileInputRef.current.click();
              }}
              disabled={submitting || pages.length >= MAX_PAGES || !!pdfFile}
              className="py-4 border-2 border-emerald-300 rounded-xl bg-emerald-50 hover:bg-emerald-100 flex flex-col items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="scanner-camera-btn"
            >
              <Camera className="w-6 h-6 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-700">{t("biology.cameraBtn")}</span>
            </button>
            <button
              onClick={() => {
                fileInputRef.current.setAttribute("accept", "image/*");
                fileInputRef.current.removeAttribute("capture");
                fileInputRef.current.click();
              }}
              disabled={submitting || pages.length >= MAX_PAGES || !!pdfFile}
              className="py-4 border-2 border-slate-300 rounded-xl bg-slate-50 hover:bg-slate-100 flex flex-col items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="scanner-gallery-btn"
            >
              <ImageIcon className="w-6 h-6 text-slate-600" />
              <span className="text-xs font-medium text-slate-700">{t("biology.galleryBtn")}</span>
            </button>
            <button
              onClick={() => {
                fileInputRef.current.setAttribute("accept", "application/pdf");
                fileInputRef.current.removeAttribute("capture");
                fileInputRef.current.click();
              }}
              disabled={submitting || !!pdfFile || pages.length > 0}
              className="py-4 border-2 border-blue-300 rounded-xl bg-blue-50 hover:bg-blue-100 flex flex-col items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="scanner-pdf-btn"
            >
              <FileText className="w-6 h-6 text-blue-600" />
              <span className="text-xs font-medium text-blue-700">{t("biology.pdfBtn")}</span>
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleAddFiles}
            className="hidden"
            data-testid="scanner-file-input"
          />

          {pages.length === 0 && !pdfFile && (
            <p className="text-center text-xs text-slate-500 pt-2">
              {t("biology.photoHint")}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={handleSubmit}
            disabled={submitting || (pages.length === 0 && !pdfFile)}
            className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
            data-testid="scanner-submit-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t("biology.sending")}
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                {pdfFile
                  ? t("biology.sendPdf")
                  : `${t("biology.sendBilan")} (${pages.length})`}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export { compressImageToJpeg, stitchImagesToPdf, MAX_PAGES };
